/**
 * Pure helpers backing `<ChatTable>` (`chat-table.tsx`, PLT-4835).
 *
 * These helpers are split into their own module — with no React, no
 * `xlsx`, and no clipboard dependencies — so the spec file can exercise
 * them without dragging the rendering pipeline into the test harness.
 * Keep this file DOM-only (the only platform globals it relies on are
 * `HTMLTableElement` / `HTMLTableRowElement` / `HTMLElement`).
 *
 * Defensive posture
 * -----------------
 * Everything in this file is designed to never throw on an LLM-generated
 * table. Concrete guards covered by `chat-table-helpers-spec.ts`:
 *   - Title absent / empty / whitespace-only / Excel-forbidden chars
 *     (`\ / ? * [ ] :`) → fall back to `Table`.
 *   - Slug input that is entirely non-alphanumeric → fall back to
 *     `table`.
 *   - Table with no `<thead>` → first row of the body is used as the
 *     header row.
 *   - Table with no rows at all → `{ headers: [], rows: [] }`.
 *   - Duplicate header labels → suffixed with ` (2)`, ` (3)`, … so
 *     `xlsx.utils.json_to_sheet` doesn't silently collapse them.
 *   - Cells containing tabs / newlines / raw HTML whitespace → collapsed
 *     to single spaces in TSV / xlsx output.
 *   - Header-less tables in the xlsx path → synthesised `Column 1`,
 *     `Column 2`, … names so the export still has labelled columns.
 */

/** Default title when the model omits the bold caption above a table. */
export const FALLBACK_TITLE = 'Table';

/** Excel's hard cap on sheet-name length; we also strip forbidden chars. */
export const EXCEL_SHEET_NAME_MAX_LEN = 31;

/**
 * Drop forbidden Excel sheet-name characters and clamp to the 31-char
 * cap. Empty / all-stripped titles fall back to `Table` so xlsx never
 * sees an empty sheet name (the writer would emit `Sheet1` anyway, but
 * the .xlsx filename also derives from the title and we'd rather not
 * download `upright-chat-.xlsx`).
 */
export function sanitizeSheetName(title: string): string {
  // The literal characters Excel disallows in sheet names: \ / ? * [ ] :
  // Also collapse runs of whitespace so trimmed titles render cleanly.
  const stripped = title
    .replace(/[\\/?*[\]:]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  const clamped = stripped.slice(0, EXCEL_SHEET_NAME_MAX_LEN);
  return clamped.length > 0 ? clamped : FALLBACK_TITLE;
}

/** Best-effort `[a-z0-9-]+` slug for the downloaded filename. */
export function slugifyTitle(title: string): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug.length > 0 ? slug : 'table';
}

/**
 * Pull visible text out of a cell — `innerText` collapses inline
 * elements (badges, link pills, tone spans) to what the user sees and
 * respects `display: none`. Empty cells return an empty string rather
 * than `undefined` so downstream serialisation stays well-typed.
 *
 * Some test harnesses (jsdom older than v14) leave `innerText`
 * undefined, in which case we fall back to `textContent` via the cast
 * below so unit tests don't fail on the DOM check.
 */
function cellText(cell: HTMLElement): string {
  // `innerText` is typed as `string` on `HTMLElement` (always present
  // in browsers) but is `undefined` in older jsdom; widen via cast so
  // the `??` fallback stays well-typed under the browser-DOM types
  // without tripping `no-unnecessary-condition`. `textContent` is
  // similarly typed `string` here even though MDN documents `string |
  // null`, so use the same widening trick.
  const widened = cell as unknown as {
    innerText: string | undefined;
    textContent: string | null;
  };
  const text = widened.innerText ?? widened.textContent ?? '';
  // Collapse internal whitespace runs — `innerText` can keep multiple
  // spaces from inline markup boundaries that aren't visible to users.
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * Make every entry in `headers` unique by suffixing duplicates with
 * ` (2)`, ` (3)`, … . xlsx's `json_to_sheet` keys by `Object.keys()`
 * and silently collapses duplicate column names into one column;
 * de-duping here keeps every column visible.
 *
 * Exported for unit testing.
 */
export function deduplicateHeaders(headers: readonly string[]): string[] {
  const seen = new Map<string, number>();
  return headers.map(header => {
    const count = (seen.get(header) ?? 0) + 1;
    seen.set(header, count);
    return count === 1 ? header : `${header} (${count})`;
  });
}

/** Parsed table content ready for TSV / xlsx serialisation. */
export interface ParsedTable {
  headers: string[];
  rows: string[][];
}

/**
 * Walk a rendered `<table>` element into a `{ headers, rows }`
 * structure. Tolerates LLM-generated tables that skip `<thead>` or
 * `<tbody>`. Never throws on a malformed input.
 *
 * Exported for unit testing.
 */
export function parseTableElement(table: HTMLTableElement): ParsedTable {
  // First gather all body rows: rows inside `<tbody>` if present, else
  // every row that is *not* inside `<thead>` or `<tfoot>`. `table.rows`
  // is a flat view per spec that includes `<thead>` and `<tfoot>` rows,
  // so we must filter them out explicitly when falling back.
  const allBodyRows: HTMLTableRowElement[] = [];
  if (table.tBodies.length > 0) {
    for (const body of Array.from(table.tBodies)) {
      for (const row of Array.from(body.rows)) allBodyRows.push(row);
    }
  } else {
    for (const row of Array.from(table.rows)) {
      const parent = row.parentElement;
      if (parent === table.tHead || parent === table.tFoot) continue;
      allBodyRows.push(row);
    }
  }

  // Header detection: prefer the explicit `<thead>`'s last row (browsers
  // sometimes wrap a single `<th>` row in `<thead>` automatically). Fall
  // back to the first body row when there is no `<thead>` — and exclude
  // that row from the body so it isn't reported twice.
  const headRow = table.tHead?.rows[table.tHead.rows.length - 1] ?? null;
  let headers: string[] = [];
  let bodyRows: HTMLTableRowElement[] = allBodyRows;
  if (headRow) {
    headers = Array.from(headRow.cells).map(c => cellText(c as HTMLElement));
  } else if (allBodyRows.length > 0) {
    headers = Array.from(allBodyRows[0].cells).map(c => cellText(c as HTMLElement));
    bodyRows = allBodyRows.slice(1);
  }

  const rows = bodyRows.map(row => Array.from(row.cells).map(c => cellText(c as HTMLElement)));
  return { headers, rows };
}

/**
 * Serialise a parsed table to TSV — tab between cells, newline between
 * rows, no terminal newline. Cell text is already trimmed by `cellText`.
 * If a cell contains a tab or newline (rare; would have to be authored
 * via raw HTML), we replace it with a space so the TSV stays one row
 * per line.
 *
 * Exported for unit testing.
 */
export function tableToTsv({ headers, rows }: ParsedTable): string {
  const sanitiseCell = (s: string) => s.replace(/[\t\r\n]+/g, ' ');
  const lines: string[] = [];
  if (headers.length > 0) lines.push(headers.map(sanitiseCell).join('\t'));
  for (const row of rows) lines.push(row.map(sanitiseCell).join('\t'));
  return lines.join('\n');
}

/**
 * Convert a parsed table to the `[{ col: cell, … }, …]` row-array shape
 * `downloadAsXlsx` expects. Numeric-looking cells (`"+12.3%"`, `"-3"`,
 * `"4.2"`) stay as strings — we don't try to coerce because the cell
 * text often carries formatting (€, ¢/$, %, parentheses for negatives)
 * the user wants preserved in the export. The .xlsx writer handles
 * mixed strings + numbers without complaint.
 *
 * Exported for unit testing.
 */
export function tableToSheetRows({ headers, rows }: ParsedTable): Array<Record<string, string>> {
  let effectiveHeaders = headers;
  if (effectiveHeaders.length === 0) {
    // No headers at all — synthesise `Column 1` / `Column 2` … so the
    // xlsx still has named columns rather than empty headers.
    const maxCols = rows.reduce((m, r) => Math.max(m, r.length), 0);
    effectiveHeaders = Array.from({ length: maxCols }, (_, i) => `Column ${i + 1}`);
  }
  const uniqueHeaders = deduplicateHeaders(effectiveHeaders);
  return rows.map(row => {
    const obj: Record<string, string> = {};
    for (let i = 0; i < uniqueHeaders.length; i++) obj[uniqueHeaders[i]] = row[i] ?? '';
    return obj;
  });
}
